import { useState, useEffect } from 'react';
import { MdClose, MdArrowBack } from 'react-icons/md';
import ERDVisualization from '../ERDEditor/ERDVisualization';
import { getStudentSubmissionsForAssignment } from '../../utils/api/AssignmentsApi';

// ==================== Content Viewers ====================

// Document viewer: renders using the actual PreviewPane component
// with OctaveProvider/PythonProvider for runnable code snippets
const DocumentViewer = ({ content, classContext }) => {
  // Lazy-load PreviewPane and providers to avoid circular deps and keep bundle light for non-document types
  const [PreviewPane, setPreviewPane] = useState(null);
  const [Providers, setProviders] = useState(null);

  const markdownContent = content?.markdown || content?.text || '';
  const documentId = content?.document_id || null;
  // Use the student_progress saved at submission time (not current DB state)
  const studentProgress = content?.student_progress || {};

  useEffect(() => {
    // Dynamic imports to avoid pulling in heavy deps when not needed
    Promise.all([
      import('../DocumentEditor/PreviewPane'),
      import('../../contexts/OctaveContext'),
      import('../../contexts/PythonContext'),
    ]).then(([previewMod, octaveMod, pythonMod]) => {
      setPreviewPane(() => previewMod.default);
      setProviders({
        OctaveProvider: octaveMod.OctaveProvider,
        PythonProvider: pythonMod.PythonProvider,
      });
    });
  }, []);

  if (!markdownContent) {
    return <p className="text-gray-400 text-sm italic">No document content</p>;
  }

  // Show loading while PreviewPane loads
  if (!PreviewPane || !Providers) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
        Loading document preview...
      </div>
    );
  }

  const { OctaveProvider, PythonProvider } = Providers;

  return (
    <OctaveProvider>
      <PythonProvider>
        <PreviewPane
          documentContent={markdownContent}
          isStudent={true}
          studentProgress={studentProgress}
          onSubmitAnswer={null}
          documentId={documentId}
          classContext={classContext}
          readOnly={true}
        />
      </PythonProvider>
    </OctaveProvider>
  );
};

// Uses the actual ERDVisualization component with zoom controls
const ERDViewer = ({ content }) => {
  if (!content?.svg && !content?.notation) {
    return <p className="text-gray-400 text-sm italic">No ERD content</p>;
  }

  return (
    <div className="space-y-4">
      {content?.svg ? (
        <ERDVisualization
          svgContent={content.svg}
          erdNotation={content.notation || ''}
          username=""
          environment=""
        />
      ) : (
        <pre className="bg-gray-100 p-3 rounded text-sm whitespace-pre-wrap font-mono">{content.notation}</pre>
      )}
    </div>
  );
};

// Code viewer with styling matching the code editor
const CodeViewer = ({ content }) => {
  if (!content?.code) {
    return <p className="text-gray-400 text-sm italic">No code content</p>;
  }

  return (
    <div>
      {content.file_name && (
        <div className="bg-gray-800 text-gray-300 px-4 py-2 text-xs font-mono rounded-t-lg border-b border-gray-700">
          {content.file_name}
        </div>
      )}
      <pre className={`bg-gray-900 text-gray-100 p-4 font-mono text-sm overflow-auto ${content.file_name ? 'rounded-b-lg' : 'rounded-lg'}`}>
        <code>{content.code}</code>
      </pre>
    </div>
  );
};

// ==================== Status Badge ====================

const StatusBadge = ({ status }) => {
  const styles = {
    submitted: 'bg-blue-100 text-blue-700',
    graded: 'bg-green-100 text-green-700',
    returned: 'bg-purple-100 text-purple-700',
    draft: 'bg-yellow-100 text-yellow-700'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'}
    </span>
  );
};

// ==================== Component Tabs (multi-component submissions) ====================

const ComponentTabs = ({ components, activeIndex, onSelect }) => (
  <div className="flex border-b border-gray-200 mb-4">
    {components.map((comp, idx) => (
      <button
        key={comp.component_id || idx}
        onClick={() => onSelect(idx)}
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
          idx === activeIndex
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        }`}
      >
        {comp.component_type?.charAt(0).toUpperCase() + comp.component_type?.slice(1) || `Component ${idx + 1}`}
      </button>
    ))}
  </div>
);

// ==================== Detail View ====================

const DetailView = ({ submission, submissionType, onBack, classContext }) => {
  const [activeComponentIdx, setActiveComponentIdx] = useState(0);

  const renderContent = (content, type) => {
    switch (type) {
      case 'document': return <DocumentViewer content={content} classContext={classContext} />;
      case 'erd': return <ERDViewer content={content} />;
      case 'code': return <CodeViewer content={content} />;
      default: return <pre className="text-sm text-gray-600">{JSON.stringify(content, null, 2)}</pre>;
    }
  };

  const hasComponents = submission.component_submissions?.length > 0;

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mb-4"
      >
        <MdArrowBack size={16} />
        Back to list
      </button>

      {/* Metadata */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Attempt #{submission.attempt_number}</h4>
          <StatusBadge status={submission.status} />
        </div>
        <div className="text-xs text-gray-500">
          {submission.submitted_at
            ? new Date(submission.submitted_at).toLocaleString()
            : 'No submission date'}
        </div>
        {submission.is_late && (
          <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
            Late{submission.late_penalty_applied ? ` (-${submission.late_penalty_applied}%)` : ''}
          </span>
        )}
        {submission.grade != null && (
          <div className="text-sm font-medium text-gray-700">
            Grade: {submission.grade}{submission.score_percentage != null ? ` (${submission.score_percentage}%)` : ''}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {hasComponents ? (
          <>
            <ComponentTabs
              components={submission.component_submissions}
              activeIndex={activeComponentIdx}
              onSelect={setActiveComponentIdx}
            />
            {renderContent(
              submission.component_submissions[activeComponentIdx]?.content,
              submission.component_submissions[activeComponentIdx]?.component_type || submissionType
            )}
          </>
        ) : (
          renderContent(submission.content, submissionType)
        )}
      </div>
    </div>
  );
};

// ==================== Main Sidebar ====================

const SubmissionHistorySidebar = ({ assignmentId, classContext, username, isOpen, onClose, submissionType }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    if (!isOpen || !assignmentId || !username) return;

    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError(null);
        setSelectedSubmission(null);
        const data = await getStudentSubmissionsForAssignment(username, assignmentId, classContext);
        // Sort by attempt_number descending (most recent first)
        const sorted = (Array.isArray(data) ? data : []).sort(
          (a, b) => (b.attempt_number || 0) - (a.attempt_number || 0)
        );
        setSubmissions(sorted);
      } catch (err) {
        console.error('Error loading submissions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [isOpen, assignmentId]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-gray-50 shadow-2xl z-50 overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900 m-0">My Submissions</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">
                {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-800 text-xs">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
              Loading submissions...
            </div>
          ) : selectedSubmission ? (
            <DetailView
              submission={selectedSubmission}
              submissionType={submissionType}
              onBack={() => setSelectedSubmission(null)}
              classContext={classContext}
            />
          ) : submissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-400">
              <p className="text-sm">No submissions yet</p>
              <p className="text-xs mt-1">Submit your work to see it here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {submissions.map((sub) => (
                <button
                  key={sub.submission_id}
                  onClick={() => setSelectedSubmission(sub)}
                  className="w-full text-left bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      Attempt #{sub.attempt_number}
                    </span>
                    <StatusBadge status={sub.status} />
                  </div>
                  <div className="text-xs text-gray-500">
                    {sub.submitted_at
                      ? new Date(sub.submitted_at).toLocaleString()
                      : 'No date'}
                  </div>
                  {sub.is_late && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                      Late
                    </span>
                  )}
                  {sub.grade != null && (
                    <div className="text-xs font-medium text-gray-600 mt-1">
                      Grade: {sub.grade}{sub.score_percentage != null ? ` (${sub.score_percentage}%)` : ''}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes slide-in-right {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out;
          }
        `}
      </style>
    </>
  );
};

export default SubmissionHistorySidebar;
